"use client";

import { useEffect, useState } from "react";
import type { SpotlightRequest } from "@/components/GlobalSearchDialog";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { SelectionBar } from "./ui";
import { readSelectedPageText, useEditActions } from "./useEditMenuGroups";
const KEYS_THAT_KEEP_THE_BAR = new Set(["Shift", "Meta", "Control", "Alt", "Tab"]);
function isInsideSelectionBar(target: EventTarget | null) {
  return target instanceof Element && !!target.closest("[data-selection-bar]");
}
interface SelectionAnchor {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
}
function selectionKey() {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return null;
  return [selection.anchorNode, selection.anchorOffset, selection.focusNode, selection.focusOffset];
}
function isSameSelection(before: ReturnType<typeof selectionKey>, after: ReturnType<typeof selectionKey>) {
  if (!before || !after) return false;
  return before.every((part, index) => part === after[index]);
}
function readSelectionAnchor(): SelectionAnchor | null {
  const text = readSelectedPageText();
  const selection = window.getSelection();
  if (!text.trim() || !selection || selection.rangeCount === 0) return null;
  const rect = selection.getRangeAt(0).getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return {
    text,
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height
  };
}
export function SelectionBarLayer({
  openSpotlight,
  ask
}: {
  openSpotlight: (request: SpotlightRequest) => void;
  ask: (question: string) => void;
}) {
  const t = useClientTranslations("desktop.contextMenu");
  const {
    copy,
    askAbout,
    search
  } = useEditActions({
    openSpotlight,
    ask
  });
  const [anchor, setAnchor] = useState<SelectionAnchor | null>(null);
  useEffect(() => {
    let selectionAtPointerDown: ReturnType<typeof selectionKey> = null;
    let pendingRead = 0;
    const hide = () => setAnchor(null);
    const onPointerDown = (event: PointerEvent) => {
      if (isInsideSelectionBar(event.target)) return;
      selectionAtPointerDown = selectionKey();
      hide();
    };
    const onMouseUp = (event: MouseEvent) => {
      if (event.button !== 0 || isInsideSelectionBar(event.target)) return;
      window.cancelAnimationFrame(pendingRead);
      pendingRead = window.requestAnimationFrame(() => {
        if (isSameSelection(selectionAtPointerDown, selectionKey())) {
          return;
        }
        setAnchor(readSelectionAnchor());
      });
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const isBarKey = isInsideSelectionBar(event.target) || KEYS_THAT_KEEP_THE_BAR.has(event.key);
      if (event.key === "Escape" || !isBarKey) hide();
    };
    const onFocusIn = (event: FocusEvent) => {
      if (!isInsideSelectionBar(event.target)) hide();
    };
    const onSelectionChange = () => {
      if (window.getSelection()?.isCollapsed) hide();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("contextmenu", hide, true);
    document.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("contextmenu", hide, true);
      document.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.cancelAnimationFrame(pendingRead);
    };
  }, []);
  if (!anchor) return null;
  const runAndHide = (action: (text: string) => unknown) => () => {
    action(anchor.text);
    setAnchor(null);
  };
  return <SelectionBar anchor={anchor} label={t("selectionActions")} actions={[{
    label: t("copy"),
    glyph: "copy",
    onSelect: runAndHide(copy)
  }, {
    label: t("askMe"),
    shortcut: "⌘J",
    onSelect: runAndHide(askAbout)
  }, {
    label: t("search"),
    glyph: "search",
    onSelect: runAndHide(search)
  }]} data-sentry-element="SelectionBar" data-sentry-component="SelectionBarLayer" data-sentry-source-file="SelectionBarLayer.tsx" />;
}